import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  UserGroupIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useChannelTemplateAthletes } from '@/hooks/useChannelAthletes';
import type { PerTemplateAthleteRow } from '@/types/channel';
import { ScoreCell } from '@/components/ui/ScoreCell';
import { TrendCell } from '@/components/ui/TrendCell';
import { getErrorMessage } from '@/utils/errors';

// ─── Athlete display name fallback (same chain as Phase B) ───────────────────

function athleteLabel(row: PerTemplateAthleteRow): string {
  if (row.display_name) return row.display_name;
  if (row.email) return row.email;
  return `Athlete ${row.user_id.slice(0, 8)}`;
}

// ─── Skeleton rows for loading state ─────────────────────────────────────────

function SkeletonRow() {
  return (
    <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
      {[140, 60, 60, 60, 50, 80].map((w, i) => (
        <td key={i} style={{ padding: '12px 12px 12px 0' }}>
          <div
            style={{
              height: '14px',
              width: `${w}px`,
              borderRadius: '4px',
              background: 'var(--color-border-subtle)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

// ─── Column sort header ───────────────────────────────────────────────────────

type SortColumn = 'athlete' | 'last_score' | 'personal_best' | 'trend' | 'sessions' | 'last_eval';
type SortDirection = 'asc' | 'desc';

interface SortHeaderProps {
  label: string;
  column: SortColumn;
  current: { column: SortColumn; direction: SortDirection };
  onSort: (col: SortColumn) => void;
}

function SortHeader({ label, column, current, onSort }: SortHeaderProps) {
  const isActive = current.column === column;
  const Icon = isActive
    ? current.direction === 'asc'
      ? ChevronUpIcon
      : ChevronDownIcon
    : ChevronUpDownIcon;

  return (
    <th
      style={{
        textAlign: 'left',
        padding: '8px 12px 8px 0',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.6px',
        color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
        whiteSpace: 'nowrap',
        cursor: 'pointer',
        userSelect: 'none',
      }}
      onClick={() => onSort(column)}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
        {label}
        <Icon style={{ width: '12px', height: '12px', flexShrink: 0 }} />
      </span>
    </th>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface PerTemplateAthleteTableProps {
  channelSlug: string;
  templateId: string;
  templateName?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function PerTemplateAthleteTable({
  channelSlug,
  templateId,
  templateName,
}: PerTemplateAthleteTableProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useChannelTemplateAthletes(channelSlug, templateId);

  const [sort, setSort] = useState<{ column: SortColumn; direction: SortDirection }>({
    column: 'last_score',
    direction: 'desc',
  });

  function handleSort(column: SortColumn) {
    setSort((prev) =>
      prev.column === column
        ? { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { column, direction: 'desc' },
    );
  }

  const sortedAthletes = useMemo(() => {
    if (!data?.athletes) return [];
    const athletes = [...data.athletes];
    const { column, direction } = sort;
    const mult = direction === 'asc' ? 1 : -1;

    athletes.sort((a, b) => {
      switch (column) {
        case 'athlete': {
          const la = athleteLabel(a).toLowerCase();
          const lb = athleteLabel(b).toLowerCase();
          return mult * la.localeCompare(lb);
        }
        case 'last_score':
          return mult * ((a.last_score ?? -1) - (b.last_score ?? -1));
        case 'personal_best':
          return mult * ((a.personal_best ?? -1) - (b.personal_best ?? -1));
        case 'trend': {
          const da =
            a.last_score !== null && a.first_score !== null ? a.last_score - a.first_score : -999;
          const db =
            b.last_score !== null && b.first_score !== null ? b.last_score - b.first_score : -999;
          return mult * (da - db);
        }
        case 'sessions':
          return mult * (a.session_count - b.session_count);
        case 'last_eval':
          return mult * (new Date(a.last_eval_at).getTime() - new Date(b.last_eval_at).getTime());
        default:
          return 0;
      }
    });

    return athletes;
  }, [data, sort]);

  // ---- Loading ---------------------------------------------------------------

  if (isLoading) {
    return (
      <div style={{ overflowX: 'auto' }}>
        {templateName && (
          <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
            Athletes evaluated on <strong style={{ color: 'var(--color-text-secondary)' }}>{templateName}</strong>
          </p>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
              {['Athlete', 'Last Score', 'Personal Best', 'Trend', 'Sessions', 'Last Eval'].map(
                (label) => (
                  <th
                    key={label}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px 8px 0',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.6px',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    {label}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((i) => <SkeletonRow key={i} />)}
          </tbody>
        </table>
      </div>
    );
  }

  // ---- Error ----------------------------------------------------------------

  if (isError) {
    const msg = getErrorMessage(error);
    return (
      <p style={{ fontSize: '13px', color: 'var(--color-accent-red)', padding: '12px 0' }}>
        {msg}
      </p>
    );
  }

  // ---- Empty ----------------------------------------------------------------

  if (!data || data.athletes.length === 0) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px 24px',
          borderRadius: '10px',
          border: '1px dashed var(--color-border-subtle)',
          background: 'rgba(23, 31, 41, 0.5)',
          textAlign: 'center',
          gap: '10px',
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-dark-elevated-2)',
            color: 'var(--color-text-muted)',
          }}
        >
          <UserGroupIcon style={{ width: '20px', height: '20px' }} />
        </div>
        <p style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: 'var(--color-text-primary)' }}>
          No athletes yet
        </p>
        <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-muted)', maxWidth: '260px' }}>
          No athletes have evaluated this template yet.
        </p>
      </div>
    );
  }

  // ---- Table ----------------------------------------------------------------

  return (
    <div style={{ overflowX: 'auto' }}>
      {templateName && (
        <p style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginBottom: '12px' }}>
          Athletes evaluated on{' '}
          <strong style={{ color: 'var(--color-text-secondary)' }}>{templateName}</strong>
          {' '}— {data.total_athletes} athlete{data.total_athletes !== 1 ? 's' : ''}
        </p>
      )}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border-subtle)' }}>
            <SortHeader label="Athlete" column="athlete" current={sort} onSort={handleSort} />
            <SortHeader label="Last Score" column="last_score" current={sort} onSort={handleSort} />
            <SortHeader label="Personal Best" column="personal_best" current={sort} onSort={handleSort} />
            <SortHeader label="Trend" column="trend" current={sort} onSort={handleSort} />
            <SortHeader label="Sessions" column="sessions" current={sort} onSort={handleSort} />
            <SortHeader label="Last Eval" column="last_eval" current={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sortedAthletes.map((row) => (
            <tr
              key={row.user_id}
              style={{
                borderBottom: '1px solid var(--color-border-subtle)',
                cursor: 'pointer',
                transition: 'background-color 120ms ease',
              }}
              onClick={() => navigate(`/athletes/${row.user_id}`)}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLTableRowElement).style.backgroundColor =
                  'rgba(61, 169, 252, 0.04)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
              }}
            >
              {/* Athlete name */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <p
                  style={{
                    margin: 0,
                    fontWeight: 600,
                    color: 'var(--color-accent-blue)',
                    fontSize: '14px',
                  }}
                >
                  {row.display_name || row.email || `Athlete ${row.user_id.slice(0, 8)}`}
                </p>
                {row.display_name && row.email && (
                  <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    {row.email}
                  </p>
                )}
              </td>

              {/* Last score */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <ScoreCell value={row.last_score} />
              </td>

              {/* Personal best */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                {row.personal_best !== null ? (
                  <span
                    style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 700,
                      fontSize: '14px',
                      color: 'var(--color-accent-gold)',
                    }}
                  >
                    {Math.round(row.personal_best)}
                  </span>
                ) : (
                  <span style={{ color: 'var(--color-text-muted)', fontSize: '13px' }}>—</span>
                )}
              </td>

              {/* Trend */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <TrendCell row={row} />
              </td>

              {/* Sessions */}
              <td style={{ padding: '12px 12px 12px 0' }}>
                <span style={{ color: 'var(--color-text-primary)', fontWeight: 600 }}>
                  {row.session_count}
                </span>
              </td>

              {/* Last eval relative time */}
              <td style={{ padding: '12px 0', whiteSpace: 'nowrap' }}>
                <span style={{ color: 'var(--color-text-muted)', fontSize: '12px' }}>
                  {formatDistanceToNow(new Date(row.last_eval_at), { addSuffix: true })}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default PerTemplateAthleteTable;
